import { db } from '../../firebase';
import { collection, addDoc, updateDoc, doc, getDoc } from 'firebase/firestore';
import { telegramConfig } from './config';

class TelegramService {
  constructor() {
    // Brug værdier fra config filen
    this.botToken = telegramConfig.botToken;
    this.apiUrl = `https://api.telegram.org/bot${this.botToken}`;
    this.editingState = new Map(); // Holder styr på hvilke chats der er i redigeringstilstand
    console.log('TelegramService initialiseret med URL:', this.apiUrl);
  }

  // Tilknyt en ny Telegram chat til en bruger
  async connectTelegramChat(userId, chatId) {
    try {
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);

      if (userDoc.exists()) {
        await updateDoc(userRef, {
          telegramChatId: chatId,
          telegramConnected: true
        });
        return true;
      }
      return false;
    } catch (error) {
      console.error('Fejl ved tilknytning af Telegram chat:', error);
      throw error;
    }
  }

  // Del lange beskeder op i mindre dele hvis nødvendigt (Telegram har en grænse på 4096 tegn)
  splitLongMessage(message) {
    const MAX_LENGTH = 4000; // Lidt under grænsen for at være sikker
    if (message.length <= MAX_LENGTH) {
      return [message];
    }

    const chunks = [];
    let startPos = 0;

    while (startPos < message.length) {
      // Find sidste linjeskift inden for grænsen hvis muligt
      let endPos = startPos + MAX_LENGTH;
      if (endPos < message.length) {
        const lastNewline = message.lastIndexOf('\n', endPos);
        if (lastNewline > startPos && lastNewline - startPos > MAX_LENGTH / 2) {
          endPos = lastNewline;
        }
      } else {
        endPos = message.length;
      }

      chunks.push(message.substring(startPos, endPos));
      startPos = endPos;
    }

    return chunks;
  }

  // Send besked til Telegram chat
  async sendMessage(chatId, message, addInlineKeyboard = false) {
    try {
      console.log(`Sender besked til chatId ${chatId}`);
      
      const messageChunks = this.splitLongMessage(message);
      const results = [];
      
      for (let i = 0; i < messageChunks.length; i++) {
        const chunk = messageChunks[i];
        const isLastChunk = i === messageChunks.length - 1;
        
        const requestData = {
          chat_id: chatId,
          text: chunk,
          parse_mode: 'HTML'
        };
        
        if (isLastChunk && addInlineKeyboard) {
          requestData.reply_markup = {
            inline_keyboard: [
              [
                { text: "✏️ Rediger", callback_data: "edit" },
                { text: "🔁 Prøv igen", callback_data: "retry" }
              ],
              [
                { text: "🖼️ Tilføj billede", callback_data: "add_image" }
              ],
              [
                { text: "✅ Accepter", callback_data: "accept" }
              ]
            ]
          };
        }

        const response = await fetch(`${this.apiUrl}/sendMessage`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestData)
        });

        if (!response.ok) {
          const errorData = await response.text();
          console.error('Telegram API fejl:', response.status, errorData);
          throw new Error(`Telegram API fejl: ${response.status}`);
        }

        const data = await response.json();
        console.log(`Del ${i+1}/${messageChunks.length} sendt`);
        results.push(data);
      }
      
      return results.length === 1 ? results[0] : results;
    } catch (error) {
      console.error('Fejl ved afsendelse af Telegram besked:', error);
      throw error;
    }
  }

  // Opdater besked og fjern inline keyboard
  async updateMessage(chatId, messageId, newText) {
    try {
      console.log(`Opdaterer besked ${messageId} i chat ${chatId}`);
      
      const response = await fetch(`${this.apiUrl}/editMessageText`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          chat_id: chatId,
          message_id: messageId,
          text: newText,
          parse_mode: 'HTML'
        })
      });

      if (!response.ok) {
        const errorData = await response.text();
        console.error('Telegram API fejl ved opdatering:', response.status, errorData);
        throw new Error(`Telegram API fejl ved opdatering: ${response.status} ${errorData}`);
      }

      const data = await response.json();
      console.log('Besked opdateret:', data);
      return data;
    } catch (error) {
      console.error('Fejl ved opdatering af Telegram besked:', error);
      throw error;
    }
  }

  // Gem original besked til senere redigering
  setEditingState(chatId, messageId, originalText) {
    this.editingState.set(chatId, {
      messageId,
      originalText,
      isEditing: true
    });
  }

  // Tjek om en chat er i redigeringstilstand
  isEditing(chatId) {
    return this.editingState.has(chatId) && this.editingState.get(chatId).isEditing;
  }

  // Hent original besked og messageId for en chat
  getEditingState(chatId) {
    return this.editingState.get(chatId);
  }

  // Afslut redigeringstilstand
  clearEditingState(chatId) {
    this.editingState.delete(chatId);
  }

  // Håndter callback fra inline knapper
  async handleCallbackQuery(callbackQueryId, action, chatId, messageId, originalText) {
    try {
      // Først bekræft callback query for at fjerne loading-tilstand
      await fetch(`${this.apiUrl}/answerCallbackQuery`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          callback_query_id: callbackQueryId
        })
      });

      let responseText = '';
      
      // Håndter forskellige actions
      switch (action) {
        case 'edit':
          // Gem den oprindelige besked og sæt chatten i redigeringstilstand
          this.setEditingState(chatId, messageId, originalText);
          responseText = 'Hvad skal ændres? 🖊️\n\nSkriv dine ændringer, og jeg vil opdatere opslaget.';
          // Send ny besked i stedet for at opdatere den eksisterende
          await this.sendMessage(chatId, responseText);
          return;
        case 'retry':
          responseText = 'Du valgte at generere et nyt opslag. Genererer nyt indhold...';
          break;
        case 'accept':
          responseText = 'Du valgte at acceptere opslaget. Opslaget er klar til at blive delt!';
          break;
        default:
          responseText = 'Handling ikke genkendt.';
      }

      // For andre actions end 'edit', opdater den oprindelige besked
      return await this.updateMessage(chatId, messageId, responseText);
    } catch (error) {
      console.error('Fejl ved håndtering af callback query:', error);
      throw error;
    }
  }
}

const telegramService = new TelegramService();
export default telegramService; 